import { useState } from 'react';
import { addDays, changesOf, describeChanges, inverseOfEntry, type Contact, type LogEntry, type Person, type SchemaOptions } from '@crmsy/shared';
import { todayIso } from '../lib/today.js';
import { Modal } from './Modal.jsx';
import { QUICK_DAYS } from './DatePick.jsx';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { logTypeLabel, t, useT } from '../i18n/index.js';

const field = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent';

function submitOnCtrlEnter(fn: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      fn();
    }
  };
}

/** Labels for the app-side change keys (archived flags), localized. */
export function changeLabels() {
  return { archived: t('changes.archived'), restored: t('changes.restored'), noteRemoved: t('changes.noteRemoved'), noteRestored: t('changes.noteRestored') };
}

const nameOf = (c: Contact) => c.companyName || c.contact;

/** Follow-up quick pick shared by Followed up and Message: 3 · 5 · 7 · custom · none. */
export function NextFollowUp({ days, setDays, custom, setCustom, date }: { days: number | 'custom' | 'none'; setDays: (d: number | 'custom' | 'none') => void; custom: string; setCustom: (s: string) => void; date: string | null }) {
  const t = useT();
  return (
    <>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {QUICK_DAYS.map((d) => (
          <button key={d} type="button" className="chip" data-on={days === d} onClick={() => setDays(d)}>
            {t('date.days', { n: d })}
          </button>
        ))}
        <button type="button" className="chip" data-on={days === 'custom'} onClick={() => setDays('custom')}>{t('date.custom')}</button>
        <button type="button" className="chip" data-on={days === 'none'} onClick={() => setDays('none')}>✕ {t('date.none')}</button>
        <span className="self-center text-[11px] text-muted">{date ? `→ ${date}` : t('date.noDate')}</span>
      </div>
      {days === 'custom' && <input type="date" value={custom} onChange={(e) => setCustom(e.target.value)} className={`${field} mt-2`} />}
    </>
  );
}

/* ---------- note ---------- */

export function NoteDialog({ contact, onSave, onClose }: { contact: Contact; onSave: (text: string) => void; onClose: () => void }) {
  const t = useT();
  const [text, setText] = useState('');
  const save = () => {
    if (!text.trim()) return;
    onSave(text.trim());
    onClose();
  };
  return (
    <Modal title={t('note.title', { name: nameOf(contact) })} onClose={onClose} hint={<><kbd>Ctrl+Enter</kbd> {t('dialog.hintSave')} · <kbd>Esc</kbd> {t('dialog.hintCancel')}</>}>
      <textarea data-autofocus rows={5} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={submitOnCtrlEnter(save)} placeholder={t('note.placeholder')} className={field} />
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn" onClick={onClose}>{t('dialog.cancel')}</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={!text.trim()}>{t('note.add')}</button>
      </div>
    </Modal>
  );
}

/* ---------- followed up ---------- */

export interface FollowedUpValue {
  nextContactDate: string | null;
  nextAction: string | null | undefined;
  note: string;
}

export function NextActionSelect({ contact, schema, value, onChange, onKeyDown }: { contact: Contact; schema: SchemaOptions; value: string; onChange: (v: string) => void; onKeyDown?: (e: React.KeyboardEvent) => void }) {
  const actions = schema['Next Action'] ?? [];
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={onKeyDown} className={`${field} mt-1`}>
      <option value="">{t('common.empty')}</option>
      {actions.map((o) => (
        <option key={o.name} value={o.name}>{o.name}</option>
      ))}
      {contact.nextAction && !actions.some((o) => o.name === contact.nextAction) && <option value={contact.nextAction}>{contact.nextAction}</option>}
    </select>
  );
}

export function FollowedUpDialog({ contact, schema, onSave, onClose }: { contact: Contact; schema: SchemaOptions; onSave: (v: FollowedUpValue) => void; onClose: () => void }) {
  const t = useT();
  const today = todayIso();
  const [days, setDays] = useState<number | 'custom' | 'none'>(7);
  const [custom, setCustom] = useState(addDays(today, 14));
  const [nextAction, setNextAction] = useState<string>(contact.nextAction ?? '');
  const [note, setNote] = useState('');
  const date = days === 'none' ? null : days === 'custom' ? custom : addDays(today, days);
  const save = () => {
    if (days === 'custom' && !/^\d{4}-\d{2}-\d{2}$/.test(custom)) return;
    onSave({ nextContactDate: date, nextAction: nextAction === (contact.nextAction ?? '') ? undefined : nextAction || null, note: note.trim() });
    onClose();
  };
  return (
    <Modal title={t('followedUp.title', { name: nameOf(contact) })} onClose={onClose} hint={<><kbd>Ctrl+Enter</kbd> {t('dialog.hintSave')} · <kbd>Esc</kbd> {t('dialog.hintCancel')}</>}>
      <div className="text-xs font-medium text-muted">{t('followedUp.next')}</div>
      <NextFollowUp days={days} setDays={setDays} custom={custom} setCustom={setCustom} date={date} />

      <div className="mt-3 text-xs font-medium text-muted">{t('followedUp.nextAction')}</div>
      <NextActionSelect contact={contact} schema={schema} value={nextAction} onChange={setNextAction} />

      <div className="mt-3 text-xs font-medium text-muted">{t('followedUp.note')}</div>
      <textarea data-autofocus rows={3} value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={submitOnCtrlEnter(save)} placeholder={t('followedUp.notePlaceholder')} className={`${field} mt-1`} />
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn" onClick={onClose}>{t('dialog.cancel')}</button>
        <button type="button" className="btn btn-primary" onClick={save}>{t('followedUp.button')}</button>
      </div>
    </Modal>
  );
}

/* ---------- edit name / contact / email ---------- */

export interface EditValue {
  companyName: string;
  contact: string;
  email: string;
  phone: string;
}

export function EditDialog({ contact, onSave, onClose }: { contact: Contact; onSave: (v: EditValue) => void; onClose: () => void }) {
  const t = useT();
  const [v, setV] = useState<EditValue>({ companyName: contact.companyName, contact: contact.contact, email: contact.email ?? '', phone: contact.phone ?? '' });
  const set = (k: keyof EditValue) => (e: React.ChangeEvent<HTMLInputElement>) => setV({ ...v, [k]: e.target.value });
  const save = () => {
    if (!v.companyName.trim()) return;
    onSave({ companyName: v.companyName.trim(), contact: v.contact.trim(), email: v.email.trim(), phone: v.phone.trim() });
    onClose();
  };
  const row = (label: string, k: keyof EditValue, type = 'text', autofocus = false) => (
    <label className="block">
      <span className="text-xs font-medium text-muted">{label}</span>
      <input data-autofocus={autofocus || undefined} type={type} value={v[k]} onChange={set(k)} onKeyDown={submitOnCtrlEnter(save)} className={`${field} mt-1`} />
    </label>
  );
  return (
    <Modal title={t('edit.title')} onClose={onClose} hint={<><kbd>Ctrl+Enter</kbd> {t('dialog.hintSave')} · <kbd>Esc</kbd> {t('dialog.hintCancel')}</>}>
      <div className="space-y-2">
        {row(t('edit.company'), 'companyName', 'text', true)}
        {row(t('edit.contact'), 'contact')}
        {row(t('edit.email'), 'email', 'email')}
        {row(t('edit.phone'), 'phone', 'tel')}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn" onClick={onClose}>{t('dialog.cancel')}</button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={!v.companyName.trim()}>{t('dialog.save')}</button>
      </div>
    </Modal>
  );
}

/* ---------- revert confirmation (§6.4) ---------- */

export function RevertConfirm({ entry, people, onConfirm, onClose }: { entry: LogEntry; people: Person[]; onConfirm: () => void; onClose: () => void }) {
  const t = useT();
  const name = (id: string) => people.find((p) => p.id === id)?.name ?? id;
  let lines: string[];
  try {
    lines = describeChanges(changesOf(inverseOfEntry(entry)), name, changeLabels());
  } catch (e) {
    lines = [t('revert.cannot', { error: e instanceof Error ? e.message : String(e) })];
  }
  const confirm = () => {
    onConfirm();
    onClose();
  };
  // Enter confirms at picker priority so the view underneath cannot take it (it would open a panel instead).
  useKeys({ Enter: confirm }, { priority: PRIORITY.picker });
  return (
    <Modal title={t('revert.title', { type: logTypeLabel(entry.type) })} onClose={onClose} hint={<><kbd>Enter</kbd> {t('revert.hint')} · <kbd>Esc</kbd> {t('dialog.hintCancel')}</>}>
      <p className="text-sm text-muted">{t('revert.willApply')}</p>
      <ul className="mt-2 space-y-1 text-sm">
        {lines.map((l, i) => (
          <li key={i} className="rounded-lg bg-surface px-3 py-1.5">{l}</li>
        ))}
      </ul>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn" onClick={onClose}>{t('dialog.cancel')}</button>
        <button data-autofocus type="button" className="btn btn-primary" onClick={confirm}>{t('revert.button')}</button>
      </div>
    </Modal>
  );
}

/* ---------- archive confirmation ---------- */

export function ArchiveConfirm({ contact, onConfirm, onClose }: { contact: Contact; onConfirm: () => void; onClose: () => void }) {
  const t = useT();
  const confirm = () => {
    onConfirm();
    onClose();
  };
  useKeys({ Enter: confirm }, { priority: PRIORITY.picker });
  return (
    <Modal title={t('archive.title')} onClose={onClose} hint={<><kbd>Enter</kbd> {t('archive.hint')} · <kbd>Esc</kbd> {t('dialog.hintCancel')}</>}>
      <p className="text-sm">{t('archive.body', { name: nameOf(contact) })}</p>
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn" onClick={onClose}>{t('dialog.cancel')}</button>
        <button data-autofocus type="button" className="btn btn-primary" onClick={confirm}>{t('archive.button')}</button>
      </div>
    </Modal>
  );
}
